// Re-synced with LC2's current apiSlice.js (post "Security: use bearer service token for
// loyalty-cloud API"). The old `LCAPIKey` header is GONE — loyalty-cloud now requires a
// short-lived bearer service token (minted server-side during /api/verify_login, cached
// in the `lc_service_token` cookie via app/store/reducers/session.js) for
// @service_token_required routes. This is the fix for the CORS "lcapikey is not allowed"
// error: the old header this file used to send no longer belongs on this API at all.
import axios from 'axios';
import {createApi} from '@reduxjs/toolkit/query/react';

import CONSTANTS from '../../commons/constants/envConstants';
import {
    getCurrentToken,
    initializeToken,
    removeCookie,
    getLcServiceTokenCookie,
    isJwtExpired,
    clearLcServiceTokenCookie
} from '../../commons/utils/cookies';

initializeToken();

const axiosBaseQuery =
    () =>
    async ({url, method, data, params, headers}) => {
        try {
            const baseUrl = CONSTANTS.LC_API_URL;
            const sessionToken = await getCurrentToken();
            const serviceToken = getLcServiceTokenCookie();

            const configuredHeaders = {
                'X-Session-Token': sessionToken,
                'Content-Type': 'application/json',
                ...headers
            };

            // loyalty-cloud backend now requires a short-lived bearer token for @service_token_required routes.
            // This token is minted server-side during /api/verify_login and stored in the lc_service_token cookie.
            if (serviceToken) {
                if (!isJwtExpired(serviceToken)) {
                    configuredHeaders.Authorization = `Bearer ${serviceToken}`;
                } else {
                    // stale bearer token; clear it so the next auth flow can repopulate it
                    clearLcServiceTokenCookie();
                }
            }

            const result = await axios({
                url: `${baseUrl}${url}`,
                method,
                data,
                params,
                headers: configuredHeaders
            });

            return {data: result.data?.data ?? result.data};
        } catch (axiosError) {
            const err = axiosError;
            let json_error = err.response?.data ? JSON.stringify(err.response?.data) : JSON.stringify(err.message);
            json_error = json_error.toLowerCase();

            const status = err.response?.status;

            if (json_error.includes('session') || json_error.includes('magic link')) {
                removeCookie('session_token');
                removeCookie('expires_at');
                clearLcServiceTokenCookie();
                window.location.reload();
            } else if (status === 401) {
                // If bearer token auth fails, clear it and force re-auth.
                clearLcServiceTokenCookie();
                removeCookie('session_token');
                removeCookie('expires_at');
                window.location.reload();
            } else {
                return {
                    error: {
                        status,
                        data: err.response?.data || err.message
                    }
                };
            }
        }
    };

export const loyaltyCloudApi = createApi({
    reducerPath: 'loyaltyCloudApi',
    baseQuery: axiosBaseQuery(),
    endpoints: (builder) => ({
        getTeamMemberInfo: builder.query({
            // Optional `{account_sfid}` arg scopes the lookup to the account picked in the
            // dropdown. Other callers pass nothing (or a token string), which sends no params.
            query: (arg) => ({
                url: '/session_info/',
                method: 'get',
                params: arg?.account_sfid ? {account_sfid: arg.account_sfid} : undefined
            }),
            providesTags: [{type: 'Session', id: 'USER'}]
        }),
        getTeamMemberInfoByEmailOrId: builder.query({
            query: ({email, teamMemberId}) => ({
                url: '/team_member/',
                method: 'get',
                params: {email, team_member_id: teamMemberId}
            })
        }),
        updateTeamMemberInfo: builder.mutation({
            query: ({data, id}) => ({
                url: '/team_member/',
                method: 'patch',
                params: {loyalty_cloud_team_member_id: id},
                headers: {'Content-Type': 'multipart/form-data'},
                data
            })
        }),
        updateTeamMemberPersonalInfo: builder.mutation({
            query: ({data, id}) => ({
                url: '/team_member/',
                method: 'patch',
                params: {loyalty_cloud_team_member_id: id},
                headers: {'Content-Type': 'multipart/form-data'},
                data
            })
        }),
        deleteTeamMemberById: builder.mutation({
            query: (id) => ({
                method: 'delete',
                url: '/team_member/',
                params: {loyalty_cloud_team_member_id: id}
            })
        }),
        getTeamMembersByAccountId: builder.query({
            query: (id) => ({
                url: '/team_members/list/',
                method: 'get',
                params: {account_id: id}
            }),
            transformResponse: (response) =>
                response
                    .map((r) => {
                        const perms = r['property_permissions']
                            .split(',')
                            .filter((p) => p)
                            .filter((p) => p !== 'all' && p !== 'blank');

                        return {...r, name: r.first_name + ' ' + r.last_name, permissions: perms};
                    })
                    .sort((a, b) => a.name.localeCompare(b.name))
        }),
        addTeamMember: builder.mutation({
            query: (data) => ({
                method: 'post',
                url: '/team_member/',
                data,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
        }),
        subscribeReport: builder.mutation({
            query: (data) => ({
                url: `/subscribe_report/`,
                method: 'post',
                data
            })
        })
    })
});

export const {
    useLazyGetTeamMemberInfoQuery,
    useLazyGetTeamMembersByAccountIdQuery,
    useUpdateTeamMemberInfoMutation,
    useUpdateTeamMemberPersonalInfoMutation,
    useDeleteTeamMemberByIdMutation,
    useAddTeamMemberMutation,
    useSubscribeReportMutation,
    useLazyGetTeamMemberInfoByEmailOrIdQuery
} = loyaltyCloudApi;
